import { useEffect } from 'react'

/**
 * Custom hook for handling keyboard shortcuts
 * 
 * @param {Object} shortcuts - Object mapping key combinations to handlers
 * @param {Array} deps - Dependencies for the effect
 */
export const useKeyboardShortcuts = (shortcuts, deps = []) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Safety check for the event object
      if (!event || typeof event !== 'object') {
        console.warn('⚠️ Invalid keyboard event received:', event)
        return
      }
      
      // Check for each shortcut
      for (const [shortcutKey, handler] of Object.entries(shortcuts)) {
        if (isShortcutMatch(event, shortcutKey)) {
          console.log('✅ Shortcut triggered:', shortcutKey)
          event.preventDefault()
          event.stopPropagation()
          handler(event)
          return
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, deps)
}

/**
 * Check if keyboard event matches the shortcut string
 * Supports: "ctrl+shift+n", "ctrl+s", etc.
 */
const isShortcutMatch = (event, shortcutString) => {
  try {
    // Safety check: ensure event.key exists
    if (!event || !event.key) {
      return false
    }
    
    // Safety check: ensure shortcutString is valid
    if (!shortcutString || typeof shortcutString !== 'string') {
      console.warn('⚠️ Invalid shortcut string:', shortcutString)
      return false
    }
    
    const parts = shortcutString.toLowerCase().split('+')
    const key = parts.pop()
    const modifiers = parts
    
    // Check if the key matches (with safety check)
    const eventKey = event.key.toLowerCase()
    if (eventKey !== key.toLowerCase()) {
      return false
    }
    
    // Check modifiers
    const needsCtrl = modifiers.includes('ctrl')
    const needsShift = modifiers.includes('shift')
    const needsAlt = modifiers.includes('alt')
    const needsMeta = modifiers.includes('meta') || modifiers.includes('cmd')
    
    // On Mac, treat ctrl as cmd for cross-platform compatibility
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
    const ctrlPressed = isMac ? event.metaKey : event.ctrlKey
    const metaPressed = isMac ? event.ctrlKey : event.metaKey
    
    return (
      ctrlPressed === needsCtrl &&
      event.shiftKey === needsShift &&
      event.altKey === needsAlt &&
      metaPressed === needsMeta
    )
  } catch (error) {
    console.error('❌ Error in isShortcutMatch:', error, { event, shortcutString })
    return false
  }
}

export default useKeyboardShortcuts 